import React, { useEffect, useState } from "react";
import { usePatientPreConsultData } from "./patientPreConsultData";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Check, AlertCircle } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import {
  setPreConsultAddtoSummary,
  setPreConsultImagePreview,
  setShowPreConsultImagePreviewModal,
  setPreConsultationTemplate,
  setPreConsultationTranscription,
} from "@/dispatchers/patientDispatchers";

export default function PatientPreConsult() {
  const {
    darkMode: isDarkMode,
    preConsultationTemplate,
    preConsultAddtoSummary,
    consultationData,
    preConsultData,
  } = usePatientPreConsultData();

  const getImageSrcFromBase64 = (base64) => {
    if (!base64) return "";

    const raw = String(base64);
    const cleaned = raw.replace(/\s/g, "");

    if (cleaned.startsWith("data:")) return cleaned;

    const afterBase64 = cleaned.includes("base64,")
      ? cleaned.split("base64,").pop()
      : cleaned;

    let payload = afterBase64.replace(/[^A-Za-z0-9+/=]/g, "");

    while (payload.length % 4 !== 0) {
      payload += "=";
      if (payload.length > 10000) break;
    }

    const detectMime = (s) => {
      if (s.startsWith("iVBOR")) return "image/png";
      if (s.startsWith("/9j/")) return "image/jpeg";
      if (s.startsWith("R0lGOD")) return "image/gif";
      return "image/png";
    };

    const mime = detectMime(payload || afterBase64 || cleaned);
    return `data:${mime};base64,${payload}`;
  };
  const signatureControl = preConsultationTemplate?.templateSections
    ?.flatMap((s) => s.userControls || [])
    .find(
      (c) =>
        typeof c?.type === "string" &&
        c.type.toLowerCase().includes("signature"),
    );

  const signatureBase64 =
    signatureControl?.options?.[0] || signatureControl?.value;
  const signatureDataUri = signatureBase64
    ? getImageSrcFromBase64(signatureBase64)
    : null;
  const [signatureImgError, setSignatureImgError] = useState(false);

  useEffect(() => {
    const template = preConsultData?.preConsultationTemplate;
    if (
      !template ||
      !Array.isArray(template.templateSections) ||
      template.templateSections.length === 0
    ) {
      setPreConsultationTemplate(null);
      setPreConsultationTranscription("");
      return;
    }

    setPreConsultationTemplate(template);

    let transcription = "";

    template.templateSections.forEach((section) => {
      section?.userControls?.forEach((control) => {
        if (control?.label && control?.value) {
          transcription += ` ${control.label} ${control.value} `;
        }
      });
    });

    setPreConsultationTranscription(transcription.trim());
  }, [preConsultData]);
  if (
    !preConsultationTemplate ||
    !preConsultationTemplate.templateSections?.length
  ) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between mb-6">
          <h2
            className={`text-base font-medium transition-colors duration-300 ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            Pre-Consultation Assessment
          </h2>
          <div className="no-templates">
            <p>No Data found</p>
          </div>
        </div>
      </div>
    );
  }
  const handleImageClick = (img) => {
    setPreConsultImagePreview(img);
    setShowPreConsultImagePreviewModal(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2
            className={`text-base font-medium transition-colors duration-300 ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            Pre-Consultation Assessment
          </h2>
          <p
            className={`text-sm mt-1 transition-colors duration-300 ${
              isDarkMode ? "text-gray-400" : "text-gray-600"
            }`}
          >
            Patient completed: {preConsultationTemplate.templateName}
          </p>
        </div>

        <div className="flex items-center gap-3">
          {preConsultationTemplate.archiveOn && (
            <Badge variant="secondary" className="text-xs">
              Completed{" "}
              {new Date(preConsultationTemplate.archiveOn).toLocaleDateString(
                "en-GB",
              )}
            </Badge>
          )}
          <Badge
            variant="outline"
            className="text-xs text-green-600 border-green-200 bg-green-50"
          >
            ✓ All Required Completed
          </Badge>
        </div>
      </div>

      {/* AI Summary */}
      {/* <div
        className={`border rounded-lg p-4 transition-colors duration-300 ${
          isDarkMode
            ? "bg-purple-900/20 border-purple-700"
            : "bg-purple-50 border-purple-200"
        }`}
      >
        <div className="flex items-start gap-3 mb-3">
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-6 h-6 bg-purple-600 rounded-full">
              <Sparkles className="w-3 h-3 text-white" />
            </div>
            <h3
              className={`font-medium text-sm transition-colors duration-300 ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              AI Clinical Summary
            </h3>
          </div>
        </div>

        <p
          className={`text-sm leading-relaxed transition-colors duration-300 ${
            isDarkMode ? "text-gray-200" : "text-gray-700"
          }`}
        >
          {preConsultationTemplate.consultationSummary}
        </p>
      </div> */}

      {/* Sections */}
      <div className="space-y-6">
        {preConsultationTemplate.templateSections
          .filter(
            (section) =>
              section.sectionTitle?.trim().toLowerCase() !== "disclaimer",
          )
          .map((section, idx) => (
            <div
              key={idx}
              className={`border rounded-lg p-4 transition-colors duration-300 ${
                isDarkMode
                  ? "bg-gray-800 border-gray-700"
                  : "bg-white border-gray-200"
              }`}
            >
              <h3
                className={`font-medium mb-4 text-sm transition-colors duration-300 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                {section.sectionTitle}
              </h3>

              <div className="space-y-4">
                {section.userControls.map((question, index) => (
                  <div key={index} className="space-y-2">
                    <p
                      className={`text-sm font-medium transition-colors duration-300 ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      {question.label}
                      {question.required && (
                        <span className="ml-1 text-red-500">*</span>
                      )}
                    </p>

                    <div
                      className={`p-3 rounded-lg transition-colors duration-300 ${
                        isDarkMode ? "bg-gray-700" : "bg-gray-50"
                      }`}
                    >
                      {question.type === "image" &&
                      Array.isArray(question.options) ? (
                        question.options.length > 0 ? (
                          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                            {question.options.map((img, idx) =>
                              typeof img === "string" && img.trim() ? (
                                <div
                                  key={idx}
                                  className={`relative overflow-hidden rounded-lg border transition-all duration-300 hover:scale-[1.03] ${
                                    isDarkMode
                                      ? "border-gray-600 bg-gray-800"
                                      : "border-gray-200 bg-white"
                                  }`}
                                >
                                  <img
                                    src={getImageSrcFromBase64(img)}
                                    alt={`Attachment ${idx + 1}`}
                                    className="object-cover w-full h-40"
                                    loading="lazy"
                                    onClick={() =>
                                      handleImageClick(
                                        getImageSrcFromBase64(img),
                                      )
                                    }
                                  />

                                  {/* Image index overlay */}
                                  <div
                                    className={`absolute bottom-0 left-0 right-0 px-2 py-1 text-xs ${
                                      isDarkMode
                                        ? "bg-black/70 text-gray-300"
                                        : "bg-white/80 text-gray-600"
                                    }`}
                                  >
                                    Image {idx + 1} of {question.options.length}
                                  </div>
                                </div>
                              ) : null,
                            )}
                          </div>
                        ) : (
                          <p
                            className={`text-sm italic ${
                              isDarkMode ? "text-gray-400" : "text-gray-500"
                            }`}
                          >
                            No images uploaded
                          </p>
                        )
                      ) : question.type === "Signature" ? (
                        question.options && question.options.length > 0 ? (
                          <img
                            src={getImageSrcFromBase64(question.options[0])}
                            alt="Signature"
                            className="object-contain h-20"
                          />
                        ) : (
                          <p
                            className={`text-sm italic ${
                              isDarkMode ? "text-gray-400" : "text-gray-500"
                            }`}
                          >
                            No signature provided
                          </p>
                        )
                      ) : (
                        <p
                          className={`text-sm transition-colors duration-300 ${
                            isDarkMode ? "text-gray-200" : "text-gray-800"
                          }`}
                        >
                          {Array.isArray(question.value)
                            ? question.value.join(", ")
                            : question.value}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
      </div>
      {/* Disclaimer Section */}
      {preConsultationTemplate?.isIncludeDisclaimer && (
        <div
          className={`border rounded-lg p-6 transition-colors duration-300 ${
            isDarkMode
              ? "bg-gray-800 border-gray-700"
              : "bg-amber-50 border-amber-200"
          }`}
        >
          <div className="flex items-start gap-3 mb-4">
            <div
              className={`p-2 rounded-lg ${
                isDarkMode ? "bg-amber-900/40" : "bg-amber-100"
              }`}
            >
              <AlertCircle
                className={`w-5 h-5 ${
                  isDarkMode ? "text-amber-400" : "text-amber-600"
                }`}
              />
            </div>
            <div className="flex-1">
              <h3
                className={`font-semibold mb-2 text-sm transition-colors duration-300 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Patient Declaration & Consent
              </h3>
              <p
                className={`text-sm leading-relaxed transition-colors duration-300 ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                {preConsultationTemplate?.disclaimerText}
              </p>
            </div>
          </div>

          {/* Signature Section */}
          {preConsultationTemplate?.isIncludeDisclaimer && (
            <div
              className={`mt-4 pt-4 border-t transition-colors duration-300 ${
                isDarkMode ? "border-gray-700" : "border-amber-200"
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p
                    className={`text-xs font-medium mb-2 transition-colors duration-300 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Patient Signature
                  </p>
                  <div
                    className={`inline-block px-4 py-3 rounded-lg border-2 border-dashed transition-colors duration-300 ${
                      isDarkMode
                        ? "bg-gray-700 border-gray-600"
                        : "bg-white border-amber-300"
                    }`}
                  >
                    {signatureBase64 ? (
                      <div>
                        <img
                          src={signatureDataUri}
                          alt="Patient signature"
                          className="object-contain h-20"
                          onError={() => setSignatureImgError(true)}
                        />
                        {signatureImgError && (
                          <div>
                            <p className="mt-1 text-xs text-red-600">
                              Signature image could not be decoded (size:{" "}
                              {signatureBase64?.length || 0} chars)
                            </p>
                            {signatureDataUri && (
                              <a
                                href={signatureDataUri}
                                download={"signature"}
                                className="text-xs text-blue-600 underline"
                              >
                                Download raw signature
                              </a>
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      <p
                        className={`text-sm italic ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        No signature provided
                      </p>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <p
                    className={`text-xs font-medium mb-2 transition-colors duration-300 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Signed On
                  </p>
                  <div
                    className={`px-3 py-2 rounded-lg transition-colors duration-300 ${
                      isDarkMode ? "bg-gray-700" : "bg-white"
                    }`}
                  >
                    <p
                      className={`text-sm font-medium transition-colors duration-300 ${
                        isDarkMode ? "text-gray-200" : "text-gray-800"
                      }`}
                    >
                      {new Date(
                        preConsultationTemplate.updatedOn,
                      ).toLocaleDateString("en-GB", {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      })}
                    </p>
                    <p
                      className={`text-xs transition-colors duration-300 ${
                        isDarkMode ? "text-gray-400" : "text-gray-600"
                      }`}
                    >
                      {new Date(
                        preConsultationTemplate.updatedOn,
                      ).toLocaleTimeString("en-GB", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </p>
                  </div>
                </div>
              </div>

              {/* Verification Badge */}
              <div className="flex items-center gap-2 mt-4">
                <div className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-green-100 text-green-700">
                  <Check className="w-4 h-4" />
                  <span className="text-xs font-medium">
                    Electronically Signed
                  </span>
                </div>
                <span
                  className={`text-xs transition-colors duration-300 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  By {consultationData?.patientName}
                </span>
              </div>
            </div>
          )}
        </div>
      )}
      {/* Add to summary */}
      <div className="flex items-center justify-end gap-2">
        <label
          className={`text-xs transition-colors duration-300 ${
            isDarkMode ? "text-gray-300" : "text-gray-600"
          }`}
        >
          Add to final summary
        </label>
        <Switch
          checked={preConsultAddtoSummary}
          onCheckedChange={setPreConsultAddtoSummary}
        />
      </div>
    </div>
  );
}
